import { ParameterType } from "jspsych";

/**
 * Plugin poldrack-categorize, version ES Module (compatible jsPsych 7+).
 */
const info = {
  name: "poldrack-categorize",
  version: "1.0",
  parameters: {
    stimulus: {
      type: ParameterType.HTML_STRING,
      default: null,
    },
    key_answer: {
      type: ParameterType.KEY,
      default: null,
    },
    correct_text: {
      type: ParameterType.HTML_STRING,
      default: "<p>Correct!</p>",
    },
    incorrect_text: {
      type: ParameterType.HTML_STRING,
      default: "<p>Incorrect</p>",
    },
    timeout_message: {
      type: ParameterType.HTML_STRING,
      default: "<p>Respond Faster!</p>",
    },
    choices: {
      type: ParameterType.KEY,
      array: true,
      default: ["f", "j"],
    },
    trial_duration: {
      type: ParameterType.INT,
      default: 1500,
    },
    feedback_duration: {
      type: ParameterType.INT,
      default: 500,
    },
    show_stim_with_feedback: {
      type: ParameterType.BOOL,
      default: true,
    },
  },
  data: {
    // Données additionnelles
    trial_id: {
      type: ParameterType.STRING,
      default: "poldrack-categorize-trial",
    },
    correct: {
      type: ParameterType.BOOL,
      default: null,
    },
  },
};

class PoldrackCategorizePlugin {
  static info = info;

  constructor(jsPsych) {
    this.jsPsych = jsPsych;
  }

  trial(display_element, trial) {
    // Valeurs par défaut (au cas où le code de l'utilisateur ne les définit pas)
    if (typeof trial.text_answer === "undefined") {
      trial.text_answer = "";
    }
    if (typeof trial.correct_text === "undefined") {
      trial.correct_text = "<p class='feedback'>Correct</p>";
    }
    if (typeof trial.incorrect_text === "undefined") {
      trial.incorrect_text = "<p class='feedback'>Incorrect</p>";
    }
    if (typeof trial.show_stim_with_feedback === "undefined") {
      trial.show_stim_with_feedback = true;
    }
    if (typeof trial.is_html === "undefined") {
      trial.is_html = false;
    }
    if (typeof trial.force_correct_button_press === "undefined") {
      trial.force_correct_button_press = false;
    }
    if (typeof trial.prompt === "undefined") {
      trial.prompt = "";
    }
    if (typeof trial.show_feedback_on_timeout === "undefined") {
      trial.show_feedback_on_timeout = false;
    }
    if (typeof trial.timeout_message === "undefined") {
      trial.timeout_message = "<p>Please respond faster.</p>";
    }

    // Timing
    if (typeof trial.response_ends_trial === "undefined") {
      trial.response_ends_trial = false;
    }
    if (typeof trial.timing_stim === "undefined") {
      trial.timing_stim = -1; // affiche le stimulus jusqu'à la réponse par défaut
    }
    if (typeof trial.trial_duration === "undefined") {
      trial.trial_duration = -1; // pas de limite max
    }
    if (typeof trial.timing_feedback_duration === "undefined") {
      trial.timing_feedback_duration = 2000;
    }
    if (typeof trial.timing_post_trial === "undefined") {
      trial.timing_post_trial = 1000;
    }

    // Tableau pour stocker nos setTimeout, afin de pouvoir les clear si besoin
    const setTimeoutHandlers = [];

    // Convertir le stimulus en chaîne, si besoin
    trial.stimulus = String(trial.stimulus);

    // 1) Affichage du stimulus
    if (!trial.is_html) {
      // cas "image"
      const img = document.createElement("img");
      img.src = trial.stimulus;
      img.className = "jspsych-poldrack-categorize-stimulus";
      img.id = "jspsych-poldrack-categorize-stimulus";
      display_element.appendChild(img);
    } else {
      // cas "HTML"
      const stimContainer = document.createElement("div");
      stimContainer.id = "jspsych-poldrack-categorize-stimulus";
      stimContainer.className = "jspsych-poldrack-categorize-stimulus";
      stimContainer.innerHTML = trial.stimulus;
      display_element.appendChild(stimContainer);
    }

    // 2) Masquer le stimulus après `trial.timing_stim` ms, si > 0
    if (trial.timing_stim > 0) {
      const hideStim = setTimeout(() => {
        const stimEl = document.getElementById("jspsych-poldrack-categorize-stimulus");
        if (stimEl) stimEl.style.visibility = "hidden";
      }, trial.timing_stim);
      setTimeoutHandlers.push(hideStim);
    }

    // 3) Prompt additionnel ?
    if (trial.prompt !== "") {
      // on peut insérer direct en HTML, ou créer un élément <div> supplémentaire
      const promptContainer = document.createElement("div");
      promptContainer.innerHTML = trial.prompt;
      display_element.appendChild(promptContainer);
    }

    let trial_data = {};

    // 4) Callback de réponse
    const after_response = (info) => {
      // CSS "responded"
      const stimEl = document.getElementById("jspsych-poldrack-categorize-stimulus");
      if (stimEl) stimEl.classList.add("responded");

      // Annuler les timers
      for (let i = 0; i < setTimeoutHandlers.length; i++) {
        clearTimeout(setTimeoutHandlers[i]);
      }
      // Annuler l'écoute clavier
      this.jsPsych.pluginAPI.cancelAllKeyboardResponses();

      let correct = false;
      if (trial.key_answer == info.key) {
        correct = true;
      }

      // Calcul de la durée bloc/stim
      let stim_duration = trial.timing_stim;
      let block_duration = trial.trial_duration;

      if (trial.response_ends_trial && info.rt != -1) {
        block_duration = info.rt;
      }
      if (stim_duration != -1) {
        stim_duration = Math.min(block_duration, trial.timing_stim);
      } else {
        stim_duration = block_duration;
      }

      // Enregistrer les données
      trial_data = {
        rt: info.rt,
        correct: correct,
        stimulus: trial.stimulus,
        key_press: info.key,
        correct_response: trial.key_answer,
        possible_responses: trial.choices,
        stim_duration: stim_duration,
        block_duration: block_duration,
        feedback_duration: trial.timing_feedback_duration,
        timing_post_trial: trial.timing_post_trial,
      };

      const timeout = info.rt == -1;

      // Afficher feedback maintenant si trial fini (ou no response)
      if (trial.response_ends_trial || timeout) {
        display_element.innerHTML = "";
        doFeedback(correct, timeout);
      } else {
        // Sinon, on attend la fin du temps max (trial_duration)
        if (trial.timing_stim > -1) {
          const hideStimLater = setTimeout(() => {
            const stimEl2 = document.getElementById("jspsych-poldrack-categorize-stimulus");
            if (stimEl2) stimEl2.style.visibility = "hidden";
          }, trial.timing_stim - info.rt);
          setTimeoutHandlers.push(hideStimLater);

          const showFeedbackLater = setTimeout(() => {
            doFeedback(correct, timeout);
          }, trial.trial_duration - info.rt);
          setTimeoutHandlers.push(showFeedbackLater);
        } else {
          const hideAndFeedback = setTimeout(() => {
            display_element.innerHTML = "";
            doFeedback(correct, timeout);
          }, trial.trial_duration - info.rt);
          setTimeoutHandlers.push(hideAndFeedback);
        }
      }
    };

    // 5) Ecoute clavier
    this.jsPsych.pluginAPI.getKeyboardResponse({
      callback_function: after_response,
      valid_responses: trial.choices,
      rt_method: "date",
      persist: false,
      allow_held_key: false,
    });

    // 6) Fin auto si trial_duration > 0
    if (trial.trial_duration > 0) {
      const endTrialTimeout = setTimeout(() => {
        after_response({ key: -1, rt: -1 });
      }, trial.trial_duration);
      setTimeoutHandlers.push(endTrialTimeout);
    }

    // 7) Fonction feedback
    const doFeedback = (correct, timeout) => {
      if (timeout && !trial.show_feedback_on_timeout) {
        const timeoutContainer = document.createElement("div");
        timeoutContainer.id = "jspsych-poldrack-categorize-timeout";
        timeoutContainer.innerHTML = trial.timeout_message;
        display_element.appendChild(timeoutContainer);
      } else {
        // Montre le stimulus pendant le feedback, si demandé
        if (trial.show_stim_with_feedback) {
          if (!trial.is_html) {
            const img = document.createElement("img");
            img.src = trial.stimulus;
            img.className = "jspsych-poldrack-categorize-stimulus";
            img.id = "jspsych-poldrack-categorize-stimulus";
            display_element.appendChild(img);
          } else {
            const stimDiv = document.createElement("div");
            stimDiv.id = "jspsych-poldrack-categorize-stimulus";
            stimDiv.className = "jspsych-poldrack-categorize-stimulus";
            stimDiv.innerHTML = trial.stimulus;
            display_element.appendChild(stimDiv);
          }
        }

        // Ajout du feedback (correct / incorrect)
        const feedbackContainer = document.createElement("div");
        feedbackContainer.id = "jspsych-poldrack-categorize-feedback";
        feedbackContainer.innerHTML = correct
          ? trial.correct_text
          : trial.incorrect_text;
        display_element.appendChild(feedbackContainer);
      }

      // force_correct_button_press ?
      if (
        trial.force_correct_button_press &&
        correct === false &&
        ((timeout && trial.show_feedback_on_timeout) || !timeout)
      ) {
        const after_forced_response = () => {
          endTrial();
        };
        this.jsPsych.pluginAPI.getKeyboardResponse({
          callback_function: after_forced_response,
          valid_responses: [trial.key_answer],
          rt_method: "date",
          persist: false,
          allow_held_key: false,
        });
      } else {
        // Sinon, on attend feedback_duration ms puis on termine
        setTimeout(() => {
          endTrial();
        }, trial.timing_feedback_duration);
      }
    };

    // 8) Fin du trial complet
    const endTrial = () => {
      display_element.innerHTML = "";
      this.jsPsych.finishTrial(trial_data);
    };
  }
}

PoldrackCategorizePlugin.info = info;
export default PoldrackCategorizePlugin;
